import { useCallback, useEffect, useRef, useState } from 'react';
import {
    FileText,
    ExternalLink,
    MessageCircle,
    FileCode2,
    FileSpreadsheet,
    File,
    Check,
} from 'lucide-react';
import { Button } from '@rocco/components/button';
import { cn } from '@rocco/utils/cn';
import { isValidURL } from '@modules/root';
import type LibraryDownload from '@modules/product/type/library-download';
import type { FileCategoryGroup } from '@modules/product/type/file-category';
import { getFileExtension } from '@modules/product/util/classify-download';
import { formatFileSize } from '@modules/build-hub/utils/format-document-attributes';
import { useTracker } from '@archipro-website/tracker';
import { DownloadFileRow } from '~/ui-rocco/pages/detail-shared';

/** Extension badge colours based on file type (dark theme). */
const EXT_COLOURS: Record<string, string> = {
    pdf: 'bg-red-900/30 text-red-400',
    dwg: 'bg-amber-900/30 text-amber-400',
    dxf: 'bg-amber-900/30 text-amber-400',
    rvt: 'bg-violet-900/30 text-violet-400',
    rfa: 'bg-violet-900/30 text-violet-400',
    ifc: 'bg-teal-900/30 text-teal-400',
    skp: 'bg-orange-900/30 text-orange-400',
    step: 'bg-cyan-900/30 text-cyan-400',
    stp: 'bg-cyan-900/30 text-cyan-400',
    '3dm': 'bg-pink-900/30 text-pink-400',
    doc: 'bg-blue-900/30 text-blue-400',
    docx: 'bg-blue-900/30 text-blue-400',
    xls: 'bg-green-900/30 text-green-400',
    xlsx: 'bg-green-900/30 text-green-400',
    zip: 'bg-gray-800/40 text-gray-400',
};

/** Extension badge colours for the light theme (ink-on-white surfaces). */
const EXT_COLOURS_LIGHT: Record<string, string> = {
    pdf: 'bg-red-50 text-red-700',
    dwg: 'bg-amber-50 text-amber-700',
    dxf: 'bg-amber-50 text-amber-700',
    rvt: 'bg-violet-50 text-violet-700',
    rfa: 'bg-violet-50 text-violet-700',
    ifc: 'bg-teal-50 text-teal-700',
    skp: 'bg-orange-50 text-orange-700',
    step: 'bg-cyan-50 text-cyan-700',
    stp: 'bg-cyan-50 text-cyan-700',
    '3dm': 'bg-pink-50 text-pink-700',
    doc: 'bg-blue-50 text-blue-700',
    docx: 'bg-blue-50 text-blue-700',
    xls: 'bg-green-50 text-green-700',
    xlsx: 'bg-green-50 text-green-700',
    zip: 'bg-gray-100 text-gray-600',
};

/** Returns the appropriate icon for a file extension. */
const getFileIcon = (ext: string) => {
    if (
        [
            'dwg',
            'dxf',
            'rvt',
            'rfa',
            'ifc',
            'skp',
            'step',
            'stp',
            '3dm',
        ].includes(ext)
    ) {
        return FileCode2;
    }
    if (['xls', 'xlsx', 'csv'].includes(ext)) return FileSpreadsheet;
    if (['pdf', 'doc', 'docx', 'txt'].includes(ext)) return FileText;
    return File;
};

interface FileRowProps {
    /** The download data. */
    download: LibraryDownload;
    /** The tier group this file belongs to. */
    group: FileCategoryGroup;
    /** The product ID. */
    productID: number;
    /** The professional's ID. */
    professionalID: number;
    /** The professional's title. */
    professionalTitle: string;
    /** Whether the current user is logged in. */
    isLoggedIn: boolean;
    /** Whether the file is gated (requires auth or request). */
    isGated?: boolean;
    /** Callback when a gated download is attempted. */
    onGatedDownload?: () => void;
    /** Stagger delay in ms for entrance animation. Undefined = no animation. */
    entranceDelay?: number;
    /** Callback to open the enquiry modal for this file. */
    onEnquire?: (fileTitle: string) => void;
    /** `dark` (default) for the files modal; `light` for ink-on-white surfaces. */
    theme?: 'dark' | 'light';
    /** Product thumbnail shown in the revamp desktop row (light theme only). */
    thumbnailSrc?: string;
}

/**
 * Individual file row with file-type icon, name, extension badge,
 * and contextual action buttons. Supports both free and gated downloads.
 */
const FileRow = ({
    download,
    group,
    productID,
    professionalID,
    professionalTitle,
    isLoggedIn,
    isGated = false,
    onGatedDownload,
    entranceDelay,
    onEnquire,
    theme = 'dark',
    thumbnailSrc,
}: FileRowProps) => {
    const tracking = useTracker();
    const isLight = theme === 'light';

    const shouldAnimate = entranceDelay !== undefined;
    const [isVisible, setIsVisible] = useState(!shouldAnimate);

    useEffect(() => {
        if (!shouldAnimate) return;
        const timer = setTimeout(() => setIsVisible(true), entranceDelay);
        return () => clearTimeout(timer);
    }, [shouldAnimate, entranceDelay]);
    const hasBeenTracked = useRef(false);
    const [hasDownloaded, setHasDownloaded] = useState(false);

    const link = download.ExternalLink || download.InternalLink || '';
    const isExternal = isValidURL(link);
    const extension = isExternal ? '' : getFileExtension(link);
    const FileIcon = isExternal ? ExternalLink : getFileIcon(extension);
    const extColourMap = isLight ? EXT_COLOURS_LIGHT : EXT_COLOURS;
    const extColourFallback = isLight
        ? 'bg-gray-100 text-gray-600'
        : 'bg-gray-800/40 text-gray-400';
    const extColour = extension
        ? (extColourMap[extension] ?? extColourFallback)
        : extColourFallback;

    const trackDownload = useCallback(
        (eventName: 'DownloadsClick' | 'DownloadsLoggedOutClick') => {
            if (hasBeenTracked.current) return;

            tracking.log(eventName, {
                url: new URL(window.location.href),
                label: professionalTitle,
                data: {
                    ExtraData: download.Title,
                    professionalID,
                    productID,
                    fileTitle: download.Title,
                    fileID: download.ID,
                    fileCategoryGroup: group,
                    downloadGated: isGated,
                },
            });

            hasBeenTracked.current = true;
        },
        [
            tracking,
            professionalTitle,
            professionalID,
            productID,
            download.Title,
            download.ID,
            group,
            isGated,
        ],
    );

    const handleDownload = useCallback(() => {
        if (isGated && onGatedDownload) {
            trackDownload(
                isLoggedIn ? 'DownloadsClick' : 'DownloadsLoggedOutClick',
            );
            onGatedDownload();
            setHasDownloaded(true);
            return;
        }

        trackDownload('DownloadsClick');
        window.open(link, '_blank', 'noopener');
        setHasDownloaded(true);
    }, [isGated, onGatedDownload, link, trackDownload, isLoggedIn]);

    const handleEnquire = useCallback(() => {
        onEnquire?.(download.Title);
    }, [onEnquire, download.Title]);

    /** Prevents row-level click when clicking an action button. */
    const stopPropagation = useCallback(
        (e: React.MouseEvent) => e.stopPropagation(),
        [],
    );

    if (!link) return null;

    const rowAnimationStyle = shouldAnimate
        ? {
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(10px)',
              transition:
                  'opacity 0.45s cubic-bezier(0.16, 1, 0.3, 1), transform 0.45s cubic-bezier(0.16, 1, 0.3, 1)',
          }
        : undefined;

    // Revamp desktop row (Figma 1846:9068): thumbnail + title beside the
    // documented-by / file / format / size columns, a verified badge and the
    // download affordance. The legacy dark files modal and the light mobile
    // layout keep the compact single-line row (below / further down).
    if (isLight) {
        const format = isExternal
            ? 'Link'
            : extension
              ? extension.toUpperCase()
              : 'File';
        const fileType = download.FileCategory?.Title ?? 'Document';
        // Size comes from the linked internal file's byte count (FileSizeBytes).
        // External links have no server-side size, so they fall back to a dash.
        const sizeLabel = download.FileSizeBytes
            ? formatFileSize(download.FileSizeBytes)
            : '—';

        return (
            <DownloadFileRow
                title={download.Title}
                uploader={professionalTitle}
                fileLabel={fileType}
                format={format}
                size={sizeLabel}
                Icon={FileIcon}
                thumbnailSrc={thumbnailSrc}
                hasDownloaded={hasDownloaded}
                onDownload={handleDownload}
                style={rowAnimationStyle}
            />
        );
    }

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={handleDownload}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') handleDownload();
            }}
            className={cn(
                'group flex cursor-pointer items-center gap-3 px-4 py-2.5',
                'transition-colors duration-150',
                isLight ? 'hover:bg-black/5' : 'hover:bg-white/5',
            )}
            style={
                shouldAnimate
                    ? {
                          opacity: isVisible ? 1 : 0,
                          transform: isVisible
                              ? 'translateY(0)'
                              : 'translateY(10px)',
                          transition:
                              'opacity 0.45s cubic-bezier(0.16, 1, 0.3, 1), transform 0.45s cubic-bezier(0.16, 1, 0.3, 1)',
                      }
                    : undefined
            }
        >
            <div
                className={cn(
                    'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
                    'transition-colors duration-150',
                    extColour,
                )}
            >
                <FileIcon className="h-4 w-4" strokeWidth={1.75} />
            </div>

            <div className="flex min-w-0 flex-1 items-center gap-1.5">
                <p
                    className={cn(
                        'min-w-0 truncate text-sm leading-tight font-medium',
                        isLight ? 'text-[#1f1f21]' : 'text-white',
                    )}
                    title={download.Title}
                >
                    {download.Title}
                </p>
                {hasDownloaded && (
                    <Check
                        className="text-green-500 h-3.5 w-3.5 shrink-0 animate-tick-pop-in"
                        strokeWidth={3}
                    />
                )}
            </div>

            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <div className="flex items-center gap-2" onClick={stopPropagation}>
                {onEnquire && (
                    <Button
                        color="transparent"
                        size="icon"
                        className={cn(
                            'h-8 w-8 cursor-pointer',
                            'transform-gpu rounded-lg transition-all duration-200 ease-out',
                            'hover:scale-110',
                            'active:scale-95 active:duration-75',
                            isLight
                                ? 'text-black/50 hover:bg-black/10 hover:text-black'
                                : 'text-white/50 hover:bg-white/10 hover:text-white',
                        )}
                        onClick={handleEnquire}
                        aria-label="Enquire about this file"
                    >
                        <MessageCircle className="h-4 w-4" />
                    </Button>
                )}

                <Button
                    color="transparent"
                    size="icon"
                    className={cn(
                        'h-8 w-8',
                        'transform-gpu rounded-lg transition-all duration-200 ease-out',
                        'hover:scale-110',
                        'active:scale-95 active:duration-75',
                        isLight
                            ? 'text-black/50 hover:bg-black/10 hover:text-black'
                            : 'text-white/50 hover:bg-white/10 hover:text-white',
                    )}
                    onClick={handleDownload}
                    aria-label={`Download ${download.Title}`}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="h-4 w-4 transition-transform duration-200 group-hover:translate-y-px"
                    >
                        <path d="M3 19h18v2H3v-2zm10-5.828L19.071 7.1l1.414 1.414L12 17 3.515 8.515 4.929 7.1 11 13.17V2h2v11.172z" />
                    </svg>
                </Button>
            </div>
        </div>
    );
};

export default FileRow;
